import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { ChevronLeft, Play } from 'lucide-react';
import { PageHeader } from '@/components/Layout';
import { TraceView } from '@/components/TraceView';
import { SaveAsTestCase } from './agent/AgentTests';
import { canEdit, rk, useMe } from '@/lib/hooks';
import { Badge, Button, ErrorNote, Loading } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import type { TraceDetail } from '@/lib/types';

/** Only requests whose inputs were logged (and had no attached files) can be replayed. */
const replayable = (input: unknown) => {
  if (!input || typeof input !== 'object') return false;
  const i = input as Record<string, unknown>;
  if (Array.isArray(i.files) && i.files.length) return false;
  return 'variables' in i || 'messages' in i || 'input' in i;
};

export const TracePage = ({ traceId }: { traceId: string }) => {
  const router = useRouter();
  const me = useMe();
  const q = useQuery({ queryKey: rk('trace', traceId), queryFn: () => api<TraceDetail>(`/v1/traces/${traceId}`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const t = q.data!.trace;
  return (
    <>
      <button onClick={() => router.history.back()} className="mb-2 inline-flex items-center gap-1 text-xs text-ink-2 hover:text-ink">
        <ChevronLeft className="size-3.5" /> Back
      </button>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {t.agentId ? (
              <Link to="/agents/$agentId" params={{ agentId: t.agentId }} className="mono hover:underline">
                {t.agentSlug}
              </Link>
            ) : (
              <span className="mono">{t.agentSlug}</span>
            )}
            {t.source === 'playground' && <Badge tone="brand">playground</Badge>}
          </span>
        }
        subtitle={
          <span>
            {fmtDateTime(t.startedAt)} · <span className="mono">{t.id}</span>
            {t.project ? ` · ${t.project}` : ''}
          </span>
        }
        actions={
          canEdit(me.data) &&
          t.agentId &&
          replayable(t.input) && (
            <div className="flex gap-2">
              <Link to="/agents/$agentId" params={{ agentId: t.agentId }} search={{ replay: t.id }}>
                <Button variant="ghost">
                  <Play className="size-4" /> Open in playground
                </Button>
              </Link>
              <SaveAsTestCase traceId={t.id} defaultName={`${t.agentSlug} · ${fmtDateTime(t.startedAt)}`} />
            </div>
          )
        }
      />
      <TraceView detail={q.data!} />
    </>
  );
};
